import { useEffect, useRef, useCallback } from 'react'
import { useInterviewStore } from '@/store/interviewStore'
import { buildApiUrl, getApiHeaders } from '@/config/api'

interface VisualAutosaveOptions {
  stage: 'system-design' | 'whiteboard' | 'data-modeling' | 'uml'
  intervalMs?: number
}

export function useVisualAutoSave({ stage, intervalMs = 5000 }: VisualAutosaveOptions) {
  const store = useInterviewStore()
  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const lastSavedRef = useRef<string>('')
  const isSavingRef = useRef<boolean>(false)

  const saveToAPI = useCallback(async (data: any) => {
    if (isSavingRef.current) return
    
    const { interviewId } = store
    if (!interviewId) {
      console.warn(`Cannot save ${stage}: missing interviewId`)
      return
    }

    try {
      isSavingRef.current = true
      
      let apiEndpoint: string
      const requestBody: any = {
        interviewId,
        content: data.content || '',
        save_type: 'auto'
      }

      // Set API endpoint and payload based on stage - using consolidated interview-responses endpoints
      if (stage === 'system-design') {
        apiEndpoint = 'interview-responses/public/save/system_design'
        requestBody.diagram_elements = {
          placedComponents: data.placedComponents || [],
          connections: data.connections || [],
          selectedTool: data.selectedTool || 'move',
          designMode: data.designMode || 'visual'
        }
        requestBody.design_notes = data.content || ''
      } else if (stage === 'whiteboard') {
        apiEndpoint = 'interview-responses/public/save/whiteboard'
        requestBody.canvas_elements = data.canvasData ? { canvasData: data.canvasData } : null
        requestBody.canvas_size = {
          selectedTool: data.selectedTool || 'pen',
          selectedColor: data.selectedColor || '#000000',
          strokeWidth: data.strokeWidth || 2
        }
      } else if (stage === 'data-modeling') {
        apiEndpoint = 'interview-responses/public/save/data_modeling'
        // For data modeling, extract entities, relationships, comments from the stage data
        const stageData = store.getStageData('data-modeling')
        requestBody.content = stageData.content || ''
        requestBody.model_data = stageData.entities || []
        requestBody.entities_data = stageData.entities || []
        requestBody.foreign_keys_data = stageData.fkRelationships || []
        requestBody.comments_data = stageData.comments || []
        requestBody.zoom_pan_state = stageData.zoomPan || {}
      } else if (stage === 'uml') {
        apiEndpoint = 'interview-responses/public/save/uml_design'
        // For UML design, extract classes and relationships
        const stageData = store.getStageData('uml')
        requestBody.content = stageData.content || ''
        requestBody.classes_data = JSON.stringify(stageData.classes || [])
        requestBody.relationships_data = JSON.stringify(stageData.relationships || [])
        requestBody.diagram_type = 'class'
      } else {
        return // Unknown stage
      }

      console.log(`🔄 Auto-saving ${stage}...`)

      const response = await fetch(buildApiUrl(apiEndpoint), {
        method: 'POST',
        headers: {
          ...getApiHeaders(false), // Public endpoint, no auth required
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      })

      if (!response.ok) {
        const error = await response.json()
        console.error(`❌ ${stage} autosave failed:`, error)
        return
      }

      // Mark as clean in store
      store.markStageClean(stage)
      console.log(`✅ Auto-saved ${stage}`)

    } catch (error) {
      console.error(`❌ ${stage} autosave error:`, error)
    } finally {
      isSavingRef.current = false
    }
  }, [stage, store])

  const performSave = useCallback(() => {
    if (stage === 'system-design') {
      const currentData = store.getSystemDesignData()
      const dataString = JSON.stringify(currentData)
      const hasChanged = dataString !== lastSavedRef.current
      const hasContent = Boolean(
        currentData.content?.trim().length ||
        currentData.placedComponents.length ||
        currentData.connections.length
      )

      console.log(`🔍 ${stage} checking changes:`)
      console.log(`   - hasChanged: ${hasChanged}`)
      console.log(`   - isDirty: ${currentData.isDirty}`)
      console.log(`   - hasContent: ${hasContent}`)

      if (hasChanged && hasContent && currentData.isDirty) {
        console.log(`📝 ${stage} meaningful changes detected - triggering auto-save`)
        saveToAPI(currentData)
        lastSavedRef.current = dataString
      } else {
        console.log(`⏭️ ${stage} no meaningful changes - skipping API call`)
      }
      return
    }

    if (stage === 'whiteboard') {
      const currentData = store.getWhiteboardData()
      const dataString = JSON.stringify(currentData)
      const hasChanged = dataString !== lastSavedRef.current
      const hasContent = Boolean(
        currentData.content?.trim().length ||
        currentData.canvasData
      )

      console.log(`🔍 ${stage} checking changes:`)
      console.log(`   - hasChanged: ${hasChanged}`)
      console.log(`   - isDirty: ${currentData.isDirty}`)
      console.log(`   - hasContent: ${hasContent}`)

      if (hasChanged && hasContent && currentData.isDirty) {
        console.log(`📝 ${stage} meaningful changes detected - triggering auto-save`)
        saveToAPI(currentData)
        lastSavedRef.current = dataString
      } else {
        console.log(`⏭️ ${stage} no meaningful changes - skipping API call`)
      }
      return
    }

    if (stage === 'data-modeling') {
      const currentData = store.getDataModelingData()
      const dataString = JSON.stringify(currentData)
      const hasChanged = dataString !== lastSavedRef.current
      const hasContent = Boolean(
        currentData.content?.trim().length ||
        currentData.entities.length ||
        currentData.relationships.length ||
        currentData.fkRelationships.length ||
        currentData.comments.length
      )

      console.log(`🔍 ${stage} checking changes:`)
      console.log(`   - hasChanged: ${hasChanged}`)
      console.log(`   - isDirty: ${currentData.isDirty}`)
      console.log(`   - hasContent: ${hasContent}`)

      if (hasChanged && hasContent && currentData.isDirty) {
        console.log(`📝 ${stage} meaningful changes detected - triggering auto-save`)
        saveToAPI(currentData)
        lastSavedRef.current = dataString
      } else {
        console.log(`⏭️ ${stage} no meaningful changes - skipping API call`)
      }
      return
    }

    if (stage === 'uml') {
      const currentData = store.getUMLData()
      const dataString = JSON.stringify(currentData)
      const hasChanged = dataString !== lastSavedRef.current
      const hasContent = Boolean(
        currentData.content?.trim().length ||
        currentData.classes.length ||
        currentData.relationships.length
      )

      console.log(`🔍 ${stage} checking changes:`)
      console.log(`   - hasChanged: ${hasChanged}`)
      console.log(`   - isDirty: ${currentData.isDirty}`)
      console.log(`   - hasContent: ${hasContent}`)

      if (hasChanged && hasContent && currentData.isDirty) {
        console.log(`📝 ${stage} meaningful changes detected - triggering auto-save`)
        saveToAPI(currentData)
        lastSavedRef.current = dataString
      } else {
        console.log(`⏭️ ${stage} no meaningful changes - skipping API call`)
      }
    }
  }, [stage, store, saveToAPI])

  // Set up periodic auto-save every 5 seconds
  useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }

    console.log(`🟢 useVisualAutoSave: Starting 5-second periodic save for ${stage}`)
    intervalRef.current = setInterval(() => {
      console.log(`⏰ useVisualAutoSave: 5-second interval triggered for ${stage}`)
      performSave()
    }, intervalMs)

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
      console.log(`🟢 useVisualAutoSave: Periodic save stopped for ${stage}`)
    }
  }, [performSave, intervalMs, stage])

  // Store subscription disabled - only use 5-second periodic saves
  useEffect(() => {
    console.log(`🟢 useVisualAutoSave: Store subscription DISABLED for ${stage} - only 5-second periodic saves`)
    return () => {
      console.log(`🟢 useVisualAutoSave: cleanup - NO SUBSCRIPTIONS for ${stage}`)
    }
  }, [stage, performSave])

  // Handle page visibility changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        console.log(`🔄 Page hidden, triggering immediate ${stage} save...`)
        performSave()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [performSave])

  // Return current state
  const getCurrentState = useCallback(() => {
    if (stage === 'system-design') {
      return store.getSystemDesignData()
    } else if (stage === 'whiteboard') {
      return store.getWhiteboardData()
    } else if (stage === 'data-modeling' || stage === 'uml') {
      return store.getStageData(stage)
    }
    return { isDirty: false, lastSaved: null }
  }, [stage, store])

  const currentState = getCurrentState()

  return {
    isDirty: currentState.isDirty,
    lastSaved: currentState.lastSaved,
    save: performSave
  }
}
